import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useCreateCustomVariableCategory } from '@/service-library/hooks/custom-variable-categories'
import queryKeys from '@/service-library/query-keys'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type AddCustomVariableCategoryDialogProps = {
  overlay: OverlayState
  listQueryKey: QueryKey
}

type FormValues = {
  name: string
}

export default function AddCustomVariableCategoryDialog({
  overlay,
  listQueryKey,
}: AddCustomVariableCategoryDialogProps) {
  const { rootOrganization } = useCurrentAccountContext()

  const { createCustomVariableCategory } = useCreateCustomVariableCategory({
    listQueryKey,
    // needed since we use the root org to get the custom variable categories for the other tab
    sideEffectQueryKeys: [queryKeys.organizations.details()],
    onError: () => {
      showErrorSnackbar('Unable to create category. Please try again later.')
    },
  })

  const methods = useForm<FormValues>({
    defaultValues: { name: '' },
  })

  const {
    formState: { isDirty, isValid },
    reset,
  } = methods

  function handleCreateCustomVariableCategory(values: FormValues) {
    values.name = values.name.trim()
    createCustomVariableCategory({
      ...values,
      id: generateUuid(),
      org_id: rootOrganization.id,
      is_public: false,
    })
    overlay.close()
  }

  useEffect(() => {
    if (!overlay.isOpen) {
      reset()
    }
  }, [overlay.isOpen, reset])

  return (
    <Dialog title="New Category" {...overlay} maxWidth="sm">
      <PixyDocsForm
        methods={methods}
        onSubmit={handleCreateCustomVariableCategory}
      >
        <DialogContent>
          <FormTextField
            name="name"
            autoFocus
            fullWidth
            label="Name"
            required
            useControllerProps={{
              rules: {
                validate: (value) => value.trim().length > 0,
              },
            }}
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || !isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
